/* eslint-disable react/require-default-props, react/no-unused-prop-types */
import { ChartData, ChartOptions, ScatterDataPoint, Tick, TooltipItem, TooltipModel } from "chart.js"
import { useEffect, useMemo, useRef, useState } from "react"
import { Line } from "react-chartjs-2"
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types"
import { Box } from "../../Box"
import { LineChartDataset } from "../types"
import { fixedOptions } from "./options"
import * as styles from "./styles/lineChartStyles"

export type LineChartProps<TData, TLabel> = {
  annotations?: object
  datasets: LineChartDataset<TData>
  gradientColor?: string
  hasGrid?: boolean
  height?: number
  labels: TLabel[]
  maintainAspectRatio?: boolean
  title?: string
  toolTipLabelFn?: (this: TooltipModel<"line">, tooltipItem: TooltipItem<"line">) => string[] | string
  toolTipTitleFn?: (this: TooltipModel<"line">, tooltipItem: TooltipItem<"line">[]) => string[] | string
  width?: number
  yAxisMaxValue?: number
  yAxisStepSize?: number
  yTickFn?: (tickValue: number, index: number, ticks: Tick[]) => string | number | undefined
}

export const LineChartComponent = <TData = (number | ScatterDataPoint | null)[], TLabel = unknown>({
  annotations,
  datasets,
  gradientColor,
  hasGrid = true,
  height = 200,
  labels,
  maintainAspectRatio,
  title,
  toolTipLabelFn,
  toolTipTitleFn,
  width = 500,
  yAxisMaxValue,
  yAxisStepSize,
  yTickFn,
}: LineChartProps<TData, TLabel>) => {
  const chartRef = useRef<ChartJSOrUndefined<"line", TData, TLabel>>()
  const [gradient, setGradient] = useState<CanvasGradient>()
  useEffect(() => {
    if (!gradientColor) return
    const ctx = chartRef.current?.canvas?.getContext(`2d`)
    const chartHeight = chartRef.current?.height
    if (!ctx) return
    const grad = ctx.createLinearGradient(0, 0, 0, chartHeight || 400)

    grad.addColorStop(0, gradientColor)
    grad.addColorStop(1, `#fff`)
    setGradient(grad)
  }, [chartRef, gradientColor])

  const chartData = useMemo<ChartData<"line", TData, TLabel>>(
    () => ({
      labels,
      datasets: (datasets || []).map((x) => {
        const { datasetOptions, useGradient } = x
        return {
          data: x.data,
          backgroundColor: useGradient ? gradient : undefined,
          fill: useGradient && !!gradient,
          ...datasetOptions,
        }
      }),
    }),
    [labels, gradient, datasets]
  )

  // @ts-ignore
  const options = useMemo<ChartOptions<"line">>(() => {
    const yTickFixedFn = (tickValue: number): number | undefined => tickValue || undefined

    return {
      ...fixedOptions,
      scales: {
        ...fixedOptions.scales,
        x: {
          ...fixedOptions.scales?.x,
          stacked: true,
          ticks: {
            ...fixedOptions.scales?.x?.ticks,
          },
        },
        y: {
          max: yAxisMaxValue,
          ...fixedOptions.scales?.y,
          ticks: {
            ...fixedOptions.scales?.y?.ticks,
            stepSize: yAxisStepSize,
            display: true,
            callback: yTickFn || yTickFixedFn,
          },
          grid: {
            ...fixedOptions.scales?.y?.grid,
            display: hasGrid,
          },
          title: {
            display: title !== undefined,
            text: title,
            font: {
              size: 12,
            },
          },
        },
      },
      plugins: {
        ...fixedOptions.plugins,
        tooltip: {
          ...fixedOptions.plugins?.tooltip,
          callbacks: {
            label: toolTipLabelFn,
            title: toolTipTitleFn,
          },
        },
        annotation: annotations,
      },
      maintainAspectRatio,
    }
  }, [
    annotations,
    hasGrid,
    maintainAspectRatio,
    title,
    toolTipLabelFn,
    toolTipTitleFn,
    yAxisMaxValue,
    yAxisStepSize,
    yTickFn,
  ])

  return (
    <Box css={styles.root}>
      <Line<TData, TLabel> ref={chartRef} options={options} data={chartData} height={height} width={width} />
    </Box>
  )
}

LineChartComponent.defaultProps = {
  hasGrid: true,
  height: 200,
  maintainAspectRatio: true,
  title: undefined,
  toolTipTitleFn: undefined,
  width: 500,
  yAxisMaxValue: undefined,
  yAxisStepSize: undefined,
  yTickFn: undefined,
}
